import { can, clientName, dossierLabel, statusLabel } from '@clim/client-core';
import { DOSSIER_STATUSES, type DossierStatus } from '@clim/domain';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { TypeTag } from '../components/TypeTag';
import { Empty, Input, StatusBadge, SyncTag } from '../components/ui';
import { useEngine, useReady } from '../lib/session';
import { useTerritory } from '../lib/territory';

const OPEN: DossierStatus[] = ['draft', 'submitted', 'to_correct', 'validated', 'to_schedule', 'in_progress'];

export function DossiersPage() {
  const engine = useEngine();
  const { me } = useReady();
  const { territoryId, choices } = useTerritory();
  const [params, setParams] = useSearchParams();
  const view = params.get('vue');
  const filter = (params.get('statut') ?? 'open') as 'open' | 'all' | DossierStatus;
  const [q, setQ] = useState('');
  // commune / code postal du chantier (à défaut, ceux du client)
  const [city, setCity] = useState('');
  const [cp, setCp] = useState('');
  const [sort, setSort] = useState<'updated' | 'city' | 'postalCode' | 'commercial'>('updated');
  // vue des responsables : dossiers d'un commercial (« none » = sans commercial, ex. chantiers partenaires)
  const [commercial, setCommercial] = useState('');
  const commercialOf = (d: { assignments: { userId: string; userName: string; role: string }[] }) => d.assignments.find((a) => a.role === 'commercial') ?? null;
  const commercials = [
    ...new Map(engine.list('dossier').flatMap((d) => d.assignments.filter((a) => a.role === 'commercial').map((a) => [a.userId, a.userName] as const))).entries(),
  ].sort((a, b) => a[1].localeCompare(b[1], 'fr'));
  const showCommercials = me.permissions['dossier.read'] !== 'own' && commercials.length > 0;
  const placeOf = (d: { siteCity: string | null; sitePostalCode: string | null; clientId: string }) => {
    const c = engine.get('client', d.clientId);
    return d.siteCity ? { city: d.siteCity, cp: d.sitePostalCode } : { city: c?.city ?? null, cp: c?.postalCode ?? null };
  };
  const places = engine.list('dossier').map(placeOf);
  const cities = [...new Set(places.map((p) => p.city).filter((x): x is string => !!x))].sort((a, b) => a.localeCompare(b, 'fr'));
  const cps = [...new Set(places.map((p) => p.cp).filter((x): x is string => !!x))].sort();
  const setFilter = (f: string) => {
    const next = new URLSearchParams(params);
    next.set('statut', f);
    setParams(next, { replace: true });
  };

  const dossiers = useMemo(() => {
    const term = q.trim().toLowerCase();
    return engine
      .list('dossier')
      .filter((d) => !territoryId || d.territoryId === territoryId)
      .filter((d) => (view === 'mes' ? d.assignments.some((a) => a.userId === me.user.id && a.role === 'commercial') : true))
      .filter((d) => (view === 'interventions' ? d.assignments.some((a) => a.userId === me.user.id && a.role === 'technician') : true))
      .filter((d) => (filter === 'all' ? true : filter === 'open' ? OPEN.includes(d.status) : d.status === filter))
      .filter((d) => {
        if (!term) return true;
        const c = engine.get('client', d.clientId);
        return [d.reference, d.title, clientName(c), c?.phone, c?.city, d.siteCity].some((v) => v?.toLowerCase().includes(term));
      })
      .filter((d) => !city || placeOf(d).city === city)
      .filter((d) => !cp || placeOf(d).cp === cp)
      .filter((d) => !commercial || (commercial === 'none' ? !commercialOf(d) : d.assignments.some((a) => a.role === 'commercial' && a.userId === commercial)))
      .sort((a, b) => {
        if (sort === 'city') return (placeOf(a).city ?? '\uffff').localeCompare(placeOf(b).city ?? '\uffff', 'fr') || b.updatedAt.localeCompare(a.updatedAt);
        if (sort === 'commercial')
          return (commercialOf(a)?.userName ?? '\uffff').localeCompare(commercialOf(b)?.userName ?? '\uffff', 'fr') || b.updatedAt.localeCompare(a.updatedAt);
        if (sort === 'postalCode') return (placeOf(a).cp ?? '\uffff').localeCompare(placeOf(b).cp ?? '\uffff') || b.updatedAt.localeCompare(a.updatedAt);
        return b.updatedAt.localeCompare(a.updatedAt);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, engine.version, q, filter, view, territoryId, me.user.id, city, cp, sort, commercial]);

  return (
    <section>
      <div className="page-head">
        <h1>{view === 'mes' ? 'Mes dossiers' : view === 'interventions' ? 'Mes interventions' : 'Dossiers'}</h1>
        {can(me, 'dossier.create') && (
          <Link className="btn primary" to="/dossiers/nouveau">
            + Nouveau dossier
          </Link>
        )}
      </div>
      <Input type="search" placeholder="Rechercher : référence, client, téléphone, commune…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="filters">
        {showCommercials && (
          <select className="input" aria-label="Commercial" value={commercial} onChange={(e) => setCommercial(e.target.value)}>
            <option value="">Tous les commerciaux</option>
            {commercials.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
            <option value="none">Sans commercial</option>
          </select>
        )}
        <select className="input" aria-label="Commune" value={city} onChange={(e) => setCity(e.target.value)}>
          <option value="">Toutes les communes</option>
          {cities.map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </select>
        <select className="input" aria-label="Code postal" value={cp} onChange={(e) => setCp(e.target.value)}>
          <option value="">Tous les codes postaux</option>
          {cps.map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </select>
        <select className="input" aria-label="Trier par" value={sort} onChange={(e) => setSort(e.target.value as typeof sort)}>
          <option value="updated">Trier par date de mise à jour</option>
          <option value="city">Trier par commune</option>
          <option value="postalCode">Trier par code postal</option>
          {showCommercials && <option value="commercial">Trier par commercial</option>}
        </select>
        <span className="muted">{dossiers.length} dossier(s)</span>
      </div>
      <div className="chips" role="tablist">
        {(['open', ...DOSSIER_STATUSES, 'all'] as const).map((s) => (
          <button key={s} className={`chip${filter === s ? ' on' : ''}`} onClick={() => setFilter(s)}>
            {s === 'open' ? 'En cours' : s === 'all' ? 'Tous' : statusLabel(s)}
          </button>
        ))}
      </div>
      {dossiers.length === 0 ? (
        <Empty>Aucun dossier.</Empty>
      ) : (
        <ul className="list">
          {dossiers.map((d) => {
            const c = engine.get('client', d.clientId);
            const type = engine.get('dossier_type', d.dossierTypeId);
            return (
              <li key={d.id}>
                <Link to={`/dossiers/${d.id}`} className="row">
                  <div className="row-main">
                    <strong>{clientName(c)}</strong>
                    <span className="muted">
                      {d.reference ?? 'Réf. à la synchro'} · {dossierLabel(d, type?.label)}
                      {placeOf(d).city ? ` · ${[placeOf(d).cp, placeOf(d).city].filter(Boolean).join(' ')}` : ''}
                      {showCommercials && commercialOf(d) ? ` · ${commercialOf(d)!.userName}` : ''}
                      {!territoryId && choices.length > 1 && ` · ${choices.find((t) => t.id === d.territoryId)?.code ?? ''}`}
                    </span>
                    <span>
                      <TypeTag type={type} />
                      {d.partnerId && (
                        <span className="badge partner"> Partenaire : {engine.get('reason', d.partnerId)?.label ?? '—'}</span>
                      )}
                    </span>
                  </div>
                  <SyncTag state={engine.syncState('dossier', d.id)} />
                  <StatusBadge status={d.status} />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
